"use client";
import React from "react";
import Link from "next/link";
import Image, {StaticImageData} from "next/image";
import {
  SimpleGrid,
  Button,
  Circle,
  Icon,
  GridItem,
  Heading,
  Text,
  ResponsiveValue,
  Stack,
} from "@chakra-ui/react";
import {Balancer} from "react-wrap-balancer";
import {IconType} from "@onemind-services-llc/react-icons-ng";
import {
  useTextVariants,
  useSectionHeadingVariant,
} from "@/theme/components/useSectionHeading";
import {HEADING_VARIANTS} from "@/theme/components/heading";
import {BUTTON_VARIANTS} from "@/theme/components/button";
import {PlainHoverEffect} from "styles/hoverPresets";

export type CardProps = {
  icon?: IconType;

  text?: string;
  image?: string | StaticImageData;
  title: string;
  children?: React.ReactNode;
  bgColor?: string;
  borderColor?: string;
  alignDirection?: string;
  headingColor?: string;
  textColor?: string;
  button?: {label: string; link: string};
  getHoverEffect?: boolean;
  effect?: Record<string, any>;
};

export const AdvantageCard = (props: CardProps) => {
  const {
    icon,
    text,
    image,
    title,
    children,
    button,
    bgColor,
    borderColor,
    alignDirection,
    headingColor = "black",
    textColor = "text.100",
    getHoverEffect = false,
    effect = PlainHoverEffect,
  } = props;
  const textVariant = useTextVariants();
  return (
    <Stack
      p={6}
      spacing={5}
      borderColor={borderColor}
      justifyContent={"space-between"}
      align={alignDirection}
      bgColor={bgColor}
      borderRadius={"12px"}
      _hover={getHoverEffect ? effect : {}}
      height="full"
      display="flex"
      flexDirection="column"
      w="100%"
    >
      {icon && (
        <Circle
          size="60px"
          border="2px solid"
          borderColor="primary.200"
          color="primary.200"
        >
          <Icon as={icon} w={10} h={10} />
        </Circle>
      )}
      {image && (
        <Image alt={title} src={image} style={{maxHeight: "50px", width: "52px"}} />
      )}
      <Heading
        as="h1"
        variant={HEADING_VARIANTS.SUBTITLE1}
        fontWeight={600}
        color={headingColor}
        textAlign={alignDirection === "center" ? "center" : "left"}
      >
        {title}
      </Heading>
      <Stack height={"100%"} justifyContent={"space-between"}>
        <Text
          variant={textVariant}
          textAlign={alignDirection === "center" ? "center" : "left"}
          color={textColor}
          whiteSpace={"pre-line"}
        >
          {text}
        </Text>
        {children && children}
        {button && (
          <Button
            as={Link}
            href={button.link}
            variant={BUTTON_VARIANTS.IDEAL}
            w="fit-content"
            alignSelf={"center"}
            p={4}
            mt={6}
          >
            {button.label}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

const AdvantageCardList = ({
  title,
  items,
  columns = {base: 1, md: 3},
  concText,
  bgColor = "transparent",
  alignDirection = "center",
  hoverEffect = false,
  hover = PlainHoverEffect,
}: {
  title?: string;
  items: CardProps[];
  columns?: ResponsiveValue<number>;
  concText?: string;
  bgColor?: string;
  alignDirection?: string;
  hoverEffect?: boolean;
  hover?: Record<string, any>;
}) => {
  const textVariant = useTextVariants();
  const headingVariant = useSectionHeadingVariant();
  return (
    <Stack spacing={8}>
      {title && (
        <Heading as="h2" variant={headingVariant} textAlign={"center"}>
          {title}
        </Heading>
      )}
      <SimpleGrid columns={columns} spacing={12} width={"100%"}>
        {items.map((item, index) => (
          <GridItem key={index}>
            <AdvantageCard
              {...item}
              bgColor={bgColor}
              alignDirection={alignDirection}
              getHoverEffect={hoverEffect}
              effect={hover}
            />
          </GridItem>
        ))}
      </SimpleGrid>
      <Balancer>
        {concText && (
          <Text
            variant={textVariant}
            textAlign={alignDirection === "center" ? "center" : "left"}
            color={"text.100"}
            fontWeight={600}
          >
            {concText}
          </Text>
        )}
      </Balancer>
    </Stack>
  );
};

export default AdvantageCardList;
